import {DOM} from "/@lib@/commons/xml/dom.js";
import {OSkinableInit} from "/@back@/commons/basis.js";
import {Desk} from "/@lib@/commons/desk.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";

/** Méthode custom pour qu'un Element participe au peuplement d'un JSON */
export interface IFormJsonisable extends Element {
	/** Popul un objet JSON avec la valeur courante **/
	fillJson(parent: Jsonisable, root: Jsonisable): void;

	/**
	 * Extrait la valeur à partir d'un JSON pour mettre à jour le widget.
	 *
	 * @param forceClear Comportement dans le cas où la valeur n'existe pas dans parent :
	 * 	- si true, le champ doit être reseté (retour à sa valeur par défaut),
	 *  - sinon, le champ doit rester inchangé.
	 *
	 * @return true si une valeur a été extraite et la valeur modifiée.
	 */
	extractJson(parent: Dict<any>, forceClear?: boolean): boolean;
}

export function IS_formJsonisable(t: Node): t is IFormJsonisable {return t && 'fillJson' in t}

export type IControlElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export type IField = IFormJsonisable | IControlElement | HTMLFieldSetElement;

export function IS_ControlOrFieldSet(n: Node): n is IControlElement | HTMLFieldSetElement {
	return n instanceof HTMLInputElement || n instanceof HTMLTextAreaElement || n instanceof HTMLSelectElement || n instanceof HTMLFieldSetElement;
}

/** Seul les IFormJsonisable et les contrôles avec name sont considérés comme des champs */
export function IS_Field(n: Node): n is IField {
	if (IS_formJsonisable(n)) return true;
	return IS_ControlOrFieldSet(n) && n.name.length > 0;
}

export type IPendingValidity = { computeValidity(): Promise<any> };

export interface IValidableFormElement extends Element {
	checkValidity(): boolean;

	reportValidity(): boolean;

	setValidity(flags: ValidityStateFlags | null, message?: string, anchor?: HTMLElement): void;

	validity: ValidityState;

	validationMessage: string;
}

export interface IFormElement extends IFormJsonisable, IValidableFormElement {
	name?: string;
	value: any;
	disabled: boolean;
	required: boolean;
	indeterminate: boolean;

	_initializeForm(init: OFormElementInit): void;

	form: HTMLFormElement;

	formDisabledCallback?(disabled: boolean): void;

	/** Attente de l'appel à un setValidity */
	isValidatingPending: boolean;

	addPendingValidity(pending: IPendingValidity): void;

	checkAsyncValidity(): Promise<boolean>;
}

const DEFAULT_VALIDITY: ValidityState = Object.freeze({
	badInput: false,
	customError: false,
	patternMismatch: false,
	rangeOverflow: false,
	rangeUnderflow: false,
	stepMismatch: false,
	tooLong: false,
	tooShort: false,
	typeMismatch: false,
	valid: false,
	valueMissing: false
});

export interface OFormElementInit extends OSkinableInit {
	/** Nom du champ dans le formulaire, exploité par l'API IFormJsonisable */
	name?: string;
	/** État désactivé du champ */
	disabled?: boolean;
	/** État requis du champ */
	required?: boolean;
}

/** Tests de compat des navigateurs pour les MxFormElement */
Desk.compatTable.set('formElt', window.ElementInternals && ("setValidity" in window.ElementInternals.prototype));

const disabledModifiedObserver = !Desk.checkCompat('formElt') ? new MutationObserver((entries) => {
	for (const entry of entries) {
		if (entry.type == "attributes" && entry.attributeName == "disabled") {
			(entry.target as any).formDisabledCallback((entry.target as HTMLElement).hasAttribute("disabled"));
		}
	}
}) : null;

/**
 * Implémentation de base d'un élément de type champ de formulaire.
 *
 * Utilisation d'un mixin pour enrichir BaseElement et BaseElementAsync.
 *
 * Permet de définir un message de validité affichable lors de la validation d'un formulaire parent.
 * Ce message de validité ne peut s'afficher que si l'élément (ou l'ancre) est focusable.
 * Par contre, lorsque le host d'un custom est focusable, le contenu du shadow n'est plus accessible par la navigation clavier.
 * Il vaut donc mieux ancré le message de la validation sur un élément du shadow.
 */
export function MxFormElement<T extends Constructor<HTMLElement>>(Base: T): Constructor<IFormElement> & T {
	if (Desk.checkCompat('formElt')) {
		return class FormElement extends Base implements IFormElement {
			name?: string;

			/** Getter et setter de la la valeur implémenter, ce dernier doit accepter la valeur undefined */
			value: any;

			/** Propriété statique nécessaire à l'activation de l'API form */
			static get formAssociated() { return true; }

			/** Représentation interne du formulaire */
			protected _internals: ElementInternals;

			protected _pendingValidities: Set<IPendingValidity>;

			get form() { return this._internals.form; }

			/** Synchronisation de la propriété disabled avec l'attribut. La méthode formDisabledCallback sera appellé à chaque modification de l'attribut */
			get disabled(): boolean {
				return this.hasAttribute("disabled");
			}

			set disabled(val) {
				DOM.setAttrBool(this, "disabled", val);
			}

			/** Synchronisation de la propriété required avec l'attribut.
			 * Un état indéterminé a pour effet de ne pas populer le formData lors de l'envoi du formulaire
			 * FIXME : associer cet état à la pseudoclass :indeterminate */
			set indeterminate(val: boolean) {
				DOM.setAttrBool(this, "indeterminate", val);
			}

			get indeterminate(): boolean {
				return this.hasAttribute("indeterminate");
			}

			/** Synchronisation de la propriété required avec l'attribut. */
			get required(): boolean {
				return this.hasAttribute("required");
			}

			set required(val) {
				DOM.setAttrBool(this, "required", val);
				if (this._refreshValidity) this._refreshValidity();
			}

			/**
			 * Ce callback sera appelé à chaque modification de l'attribut disabled OU si un fieldset parent est désactivé.
			 * Il est également appelé automatiquement lorsque le custom est attaché si l'attribut disabled est présent.
			 */
			formDisabledCallback?(disabled: boolean): void;

			/**
			 * Méthode d'initialisation du formulaire a appellé en fin de l'initialisation car elle peut entrainer l'appel à formDisabledCallback */
			_initializeForm(init: OFormElementInit) {
				this._internals = this.attachInternals();
				if (init.name) this.name = init.name;
				if (init.disabled) this.disabled = true;
				if (init.required) this.required = true;
			}

			extractJson(parent: Dict<any>): boolean {
				if (!this.name) return false;
				this.value = parent[this.name];
			}

			fillJson(parent: any, root: any): void {
				if (!this.name || this.indeterminate) return;
				parent[this.name] = this.value;
			}


			/** Mise à jour de l'état de validité du champ. Celui ci doit être défini après l'intialisation. */
			protected _refreshValidity?(): void;

			checkValidity() {
				return this._internals.checkValidity();
			}

			reportValidity() {
				return this._internals.reportValidity();
			}

			setValidity(flags: ValidityStateFlags, message?: string, anchor?: HTMLElement): void {
				return this._internals.setValidity(flags, message, anchor);
			}

			get validity() {
				return this._internals.validity;
			}

			get validationMessage() {
				return this._internals.validationMessage;
			}

			get isValidatingPending() {
				return this._pendingValidities?.size > 0
			}

			/** Attente de l'appel à un setValidity */
			addPendingValidity(pending: IPendingValidity): void {
				if (!this._pendingValidities) this._pendingValidities = new Set();
				this._pendingValidities.add(pending);
			}

			async checkAsyncValidity(): Promise<boolean> {
				while (this._pendingValidities?.size) {
					const currentValidities = Array.from(this._pendingValidities);
					await Promise.all(currentValidities.map((current) => current.computeValidity()));
					for (const validity of currentValidities) this._pendingValidities.delete(validity);
				}
				return this.checkValidity();
			}
		};
	} else {
		/**
		 * Implémentation alternative  du FormElement lorsque l'API ElementInternals n'est pas disponible
		 * Limitations :
		 *  - L'état disabled des parents n'est pas pris en charge à l'initialisation
		 *  - Il faut passer par FORMS.checkValidity/reportValidity/setFieldsetDisabled pour interagir avec les parents
		 *  - Le setValidity doit toujours avoir une ancre de type contrôle
		 */

		return class FormElementFallback extends Base implements IFormElement {
			name?: string;

			/** Getter et setter de la la valeur implémenter, ce dernier doit accepter la valeur undefined */
			value: any;

			_validity: ValidityState = DEFAULT_VALIDITY;

			_validationMessage: string;

			_validityAnchor: HTMLElement;

			protected _pendingValidities: Set<IPendingValidity>;

			get form() { return DOM.findParent(this, null, IS_Form); }

			/** Synchronisation de la propriété disabled avec l'attribut. La méthode formDisabledCallback sera appellé à chaque modification de l'attribut */
			get disabled(): boolean {
				return this.hasAttribute("disabled");
			}

			set disabled(val) {
				DOM.setAttrBool(this, "disabled", val);
			}

			/** Synchronisation de la propriété required avec l'attribut.
			 * Un état indéterminé a pour effet de ne pas populer le formData lors de l'envoi du formulaire
			 * FIXME : associer cet état à la pseudoclass :indeterminate */
			set indeterminate(val: boolean) {
				DOM.setAttrBool(this, "indeterminate", val);
			}

			get indeterminate(): boolean {
				return this.hasAttribute("indeterminate");
			}

			/** Synchronisation de la propriété required avec l'attribut. */
			get required(): boolean {
				return this.hasAttribute("required");
			}

			set required(val) {
				DOM.setAttrBool(this, "required", val);
				if (this._refreshValidity) this._refreshValidity();
			}

			/**
			 * Ce callback sera appelé à chaque modification de l'attribut disabled OU si un fieldset parent est désactivé.
			 * Il est également appelé automatiquement lorsque le custom est attaché si l'attribut disabled est présent.
			 */
			formDisabledCallback?(disabled: boolean): void;

			/**
			 * Méthode d'initialisation du formulaire a appellé en fin de l'initialisation car elle peut entrainer l'appel à formDisabledCallback */
			_initializeForm(init: OFormElementInit) {
				if (this.formDisabledCallback) {
					if (this.hasAttribute('disabled')) this.formDisabledCallback(true);
					disabledModifiedObserver.observe(this, {attributes: true});
				}
				if (init.name) this.name = init.name;
				if (init.disabled) this.disabled = true;
				if (init.required) this.required = true;
			}

			extractJson(parent: Dict<any>): boolean {
				if (!this.name) return false;
				this.value = parent[this.name];
			}

			fillJson(parent: any, root: any): void {
				if (!this.name || this.indeterminate) return;
				parent[this.name] = this.value;
			}


			/** Mise à jour de l'état de validité du champ. Celui ci doit être défini après l'initialisation. */
			protected _refreshValidity?(): void;

			checkValidity() {
				for (const key in DEFAULT_VALIDITY) {
					if ((this._validity as any as Dict<boolean>)[key]) return false;
				}
				return true;
			}

			reportValidity() {
				if (!this.checkValidity()) {
					if (this._validityAnchor && IS_ControlOrFieldSet(this._validityAnchor)) {
						const customValidity = this._validityAnchor.validationMessage;
						this._validityAnchor.setCustomValidity(this.validationMessage);
						this._validityAnchor.reportValidity();
						this._validityAnchor.setCustomValidity(customValidity);
					} else {
						console.error("The validity with fallback implementation of FormElement must have a control anchor.");
					}
					return false;
				}
				return true;
			}

			setValidity(flags: ValidityStateFlags, message?: string, anchor?: HTMLElement): void {
				if (flags) this._validity = Object.freeze(Object.assign(Object.create(DEFAULT_VALIDITY), flags));
				else this._validity = DEFAULT_VALIDITY;
				this._validationMessage = message;
				this._validityAnchor = anchor;
			}

			get validity() {
				return this._validity;
			}

			get validationMessage() {
				return this._validationMessage;
			}

			get isValidatingPending() {
				return this._pendingValidities?.size > 0
			}

			/** Attente de l'appel à un setValidity */
			addPendingValidity(pending: IPendingValidity): void {
				if (!this._pendingValidities) this._pendingValidities = new Set();
				this._pendingValidities.add(pending);
			}

			async checkAsyncValidity(): Promise<boolean> {
				while (this._pendingValidities?.size) {
					const currentValidities = Array.from(this._pendingValidities);
					await Promise.all(currentValidities.map((current) => current.computeValidity()));
					for (const validity of currentValidities) this._pendingValidities.delete(validity);
				}
				return this.checkValidity();
			}
		}
	}
}

export namespace FORMS {

	/** Parcourt la descendance de from pour peupler le json.*/
	export function formToJson(from: Node, parent?: Jsonisable, root?: Jsonisable): Jsonisable {
		if (!parent) parent = {};
		if (!root) root = parent;

		let tag = DOM.findNext(from, from, IS_Field);
		while (tag) {
			fieldToJson(tag, parent, root);
			tag = DOM.findNextUncle(tag, from, IS_Field);
		}
		return parent;
	}

	/** Peuple le json par un élément donné. */
	export function fieldToJson(tag: IField, parent: Jsonisable, root: Jsonisable): Jsonisable {
		if (IS_formJsonisable(tag)) {
			tag.fillJson(parent, root);
		} else if (tag instanceof HTMLInputElement) {
			if (tag.type === 'radio') {
				if (tag.checked) parent[tag.name] = tag.value;
			} else if (tag.type === 'checkbox') {
				parent[tag.name] = tag.checked;
			} else {
				parent[tag.name] = tag.value;
			}
		} else if (tag instanceof HTMLTextAreaElement) {
			parent[tag.name] = tag.value;
		} else if (tag instanceof HTMLSelectElement) {
			if (tag.multiple) {
				const array = parent[tag.name] = [] as string[];
				for (const o of tag.selectedOptions) array.push((o as HTMLOptionElement).value)
			} else {
				parent[tag.name] = tag.value;
			}
		} else if (tag instanceof HTMLFieldSetElement) {
			const newP = parent[tag.name] = {};
			formToJson(tag, newP, root);
		}
	}

	/**
	 * Remplit les champs d'un formulaire par un json donné.
	 * @param forceClear : force l'affectation du champ avec une valeur vide, même si le param "parent" ne définit pas cette valeur
	 */
	export function jsonToForm(parent: Dict<any>, elem: ParentNode, dispatchChange = false, forceClear = false) {
		for (const tag of elem.children) {
			if (IS_Field(tag)) {
				if (IS_formJsonisable(tag)) {
					const changed = tag.extractJson(parent, forceClear);
					if (tag instanceof HTMLInputElement) tag.setCustomValidity("");//raz de la validité
					if (changed && dispatchChange) dispatchChangeEvent(tag);
				} else if (tag instanceof HTMLInputElement) {
					if (tag.name in parent) {
						tag.setCustomValidity("");//raz de la validité
						if (tag.type === 'radio') {
							if (tag.value === parent[tag.name]) tag.checked = true;
						} else if (tag.type === 'checkbox') {
							tag.checked = parent[tag.name] != false;
						} else {
							tag.value = parent[tag.name];
						}
						if (dispatchChange) dispatchChangeEvent(tag);
					} else if (forceClear) {
						tag.setCustomValidity("");//raz de la validité
						tag.checked = undefined;
						if (dispatchChange) dispatchChangeEvent(tag);
					}
				} else if (tag instanceof HTMLTextAreaElement) {
					if (tag.name in parent) {
						tag.setCustomValidity("");//raz de la validité
						tag.textContent = parent[tag.name];
						if (dispatchChange) dispatchChangeEvent(tag);
					} else if (forceClear) {
						tag.setCustomValidity("");//raz de la validité
						tag.textContent = undefined;
						if (dispatchChange) dispatchChangeEvent(tag);
					}
				} else if (tag instanceof HTMLSelectElement) {
					if (tag.name in parent) {
						tag.setCustomValidity("");//raz de la validité
						const value = parent[tag.name];
						if (tag.multiple && Array.isArray(value)) {
							for (const option of tag.options) {
								if (value.includes(option.value)) option.selected = true;
							}
						} else {
							tag.value = value;
						}
						if (dispatchChange) dispatchChangeEvent(tag);
					} else if (forceClear) {
						tag.setCustomValidity("");//raz de la validité
						tag.value = undefined;
					}
				} else if (tag instanceof HTMLFieldSetElement) {
					if (tag.name in parent || forceClear) jsonToForm(parent[tag.name], tag, dispatchChange, forceClear);
				}
			} else {
				jsonToForm(parent, tag, dispatchChange, forceClear);
			}
		}
	}

	export function checkValidity(root: HTMLElement, excludeRoot?: boolean): boolean {
		if (root instanceof HTMLFormElement) {
			if (!root.checkValidity()) return false;

			// Parcours des custom dans le cas où ElementInternals n'est pas disponible
			if (!Desk.checkCompat('formElt')) {
				let elem = DOM.findNext(root, root, IS_CustomFormElement);
				while (elem) {
					if (!elem.checkValidity()) return false;
					elem = DOM.findNext(elem, root, IS_CustomFormElement);
				}
			}
			return true;
		} else {
			if (!excludeRoot && IS_ValidableFormElement(root) && !root.checkValidity()) return false;
			let elem = DOM.findNext(root, root, IS_ValidableFormElement);
			while (elem) {
				if (!elem.checkValidity()) return false;
				elem = DOM.findNext(elem, root, IS_ValidableFormElement);
			}
			return true;
		}
	}

	export async function checkAsyncValidity(root: HTMLElement, excludeRoot?: boolean): Promise<boolean> {
		// Récupération de la liste des contrôles en attente de validation
		function getPendingValControls() {
			const controls: IFormElement[] = [];
			if (!excludeRoot && IS_CustomFormElement(root) && root.isValidatingPending) controls.push(root);
			let elem = DOM.findNext(root, root, IS_CustomFormElement);
			while (elem) {
				if (elem.isValidatingPending) controls.push(elem);
				elem = DOM.findNext(elem, root, IS_CustomFormElement);
			}
			return controls;
		}

		// Attente de la validité de l'ensemble des contrôles
		let pendingValControls = getPendingValControls();
		while (pendingValControls.length) {
			await Promise.all(pendingValControls.map((control) => control.checkAsyncValidity()));
			pendingValControls = getPendingValControls();
		}

		return FORMS.checkValidity(root, excludeRoot);
	}

	/**
	 * Comportement étrange : si la racine n'est pas un formulaire, les customs validables doivent être focusables, sinon l'erreur suivante apparaît :
	 * An invalid form control with name='' is not focusable.
	 */
	export function reportValidity(root: HTMLElement): boolean {
		if (root instanceof HTMLFormElement) {
			if (!root.reportValidity()) return false;

			// Parcours des custom dans le cas où ElementInternals n'est pas disponible
			if (!Desk.checkCompat('formElt')) {
				let elem = DOM.findNext(root, root, IS_CustomFormElement);
				while (elem) {
					if (!elem.reportValidity()) return false;
					elem = DOM.findNext(elem, root, IS_CustomFormElement);
				}
			}
			return true;
		} else {
			if (IS_ValidableFormElement(root) && !root.reportValidity()) return false;
			let elem = DOM.findNext(root, root, IS_ValidableFormElement);
			while (elem) {
				if (!elem.reportValidity()) return false;
				elem = DOM.findNext(elem, root, IS_ValidableFormElement);
			}
			return true;
		}
	}

	export function setFieldsetDisabled(fieldSet: HTMLFieldSetElement, disabled: boolean): boolean {
		fieldSet.disabled = disabled;
		if (!Desk.checkCompat('formElt')) {
			let elem = DOM.findNext(fieldSet, fieldSet, IS_CustomFormElement);
			while (elem) {
				elem.disabled = disabled;
				elem = DOM.findNext(elem, fieldSet, IS_CustomFormElement);
			}
			return true;
		}
	}

	export function findFirstFocusable(form: HTMLElement): HTMLElement {
		//on privilégie les input puis tous les éléments focusbale visible.
		return DOMSH.findFlatNext(form, form, (n): n is HTMLElement => (n instanceof HTMLInputElement || n instanceof HTMLTextAreaElement) && DOM.IS_focusable(n))
			|| DOMSH.findFlatNext(form, form, (n): n is HTMLElement => DOM.IS_focusable(n));
	}
}

function dispatchChangeEvent(elem: IField) {
	const ev = document.createEvent("HTMLEvents");
	ev.initEvent("change", false, true);
	elem.dispatchEvent(ev);
}

export const IS_Form = function (elt: any): elt is HTMLFormElement {return elt instanceof HTMLFormElement};

export const IS_CustomFormElement = function (elt: any): elt is IFormElement {return 'setValidity' in elt};

export const IS_ValidableFormElement = function (elt: any): elt is IValidableFormElement {return 'checkValidity' in elt};